import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';

const API_URL = process.env.REACT_APP_API_URL;
const EMP_API = `${API_URL}/api/employees`;

const columns = [
  { key: 'employee_id', label: 'Employee ID', width: 120 },
  { key: 'name', label: 'Name', width: 120 },
  { key: 'email', label: 'Email', width: 200 },
  { key: 'position', label: 'Position', width: 100 },
  { key: 'ctc', label: 'CTC', width: 90 },
  { key: 'actual_salary', label: 'Actual Salary', width: 90 },
  { key: 'basic', label: 'Basic', width: 90 },
  { key: 'hra', label: 'House Rent Allowance', width: 110 },
  { key: 'food_allowance', label: 'Food Allowance', width: 110 },
  { key: 'engagement_fund', label: 'Employee Engagement Fund', width: 110 },
  { key: 'telephone_internet', label: 'Telephone And Internet Allow', width: 110 },
  { key: 'total_allowances', label: 'Total Allowances', width: 110 },
  { key: 'gross_salary', label: 'Gross Salary', width: 90 },
  { key: 'tds_rate', label: 'TDS Rate', width: 80 },
  { key: 'tds', label: 'TDS', width: 80 },
  { key: 'net_salary', label: 'Net Salary', width: 90 },
  { key: 'bank_name', label: 'Bank Name', width: 120 },
  { key: 'branch', label: 'Branch', width: 100 },
  { key: 'bank_account_number', label: 'Bank Account Number', width: 120 },
  { key: 'arrears', label: 'Arrears', width: 80 },
  { key: 'leave_balance', label: 'Leave Balance', width: 90 }
];

const fieldLabels = {
  employee_id: 'Employee ID',
  name: 'Name',
  email: 'Email',
  position: 'Position',
  ctc: 'CTC',
  actual_salary: 'Actual Salary',
  basic: 'Basic',
  hra: 'House Rent Allowance',
  food_allowance: 'Food Allowance',
  engagement_fund: 'Employee Engagement Fund',
  telephone_internet: 'Telephone And Internet Allow',
  total_allowances: 'Total Allowances',
  gross_salary: 'Gross Salary',
  tds_rate: 'TDS Rate',
  tds: 'TDS',
  net_salary: 'Net Salary',
  bank_name: 'Bank Name',
  branch: 'Branch',
  bank_account_number: 'Bank Account Number',
  arrears: 'Arrears',
  leave_balance: 'Leave Balance'
};

const emptyRow = Object.fromEntries(Object.keys(fieldLabels).map(k => [k, k === 'tds_rate' ? '10' : '']));

// Helper to calculate salary components
const calculateSalaryFields = (row) => {
  const ctc = parseFloat(row.ctc) || 0;
  const food = parseFloat(row.food_allowance) || 0;
  const engagement = parseFloat(row.engagement_fund) || 0;
  const telephone = parseFloat(row.telephone_internet) || 0;
  const basic = ctc * 0.4;
  const hra = basic * 0.4;
  const total_allowances = food + engagement + telephone;
  const gross_salary = ctc + total_allowances;
  const tds_rate = parseFloat(row.tds_rate) || 0;
  const tds = (gross_salary * tds_rate)/100;
  const net_salary = gross_salary - tds;
  return {
    ...row,
    basic: basic ? basic.toFixed(2) : '',
    hra: hra ? hra.toFixed(2) : '',
    total_allowances: total_allowances ? total_allowances.toFixed(2) : '',
    gross_salary: gross_salary ? gross_salary.toFixed(2) : '',
    tds: tds ? tds.toFixed(2) : '',
    net_salary: net_salary ? net_salary.toFixed(2) : '',
  };
};

export default function EmployeeManagement() {
  const [employees, setEmployees] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRow, setModalRow] = useState({});
  const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'add'

  const fetchEmployees = async () => {
    setLoading(true);
    const res = await fetch(EMP_API);
    const data = await res.json();
    setEmployees(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleEdit = (emp) => {
    setEditId(emp.id);
    setEditRow(emp);
  };

  const handleChange = (e) => {
    setEditRow({ ...editRow, [e.target.name]: e.target.value });
  };

  const handleSave = async (id) => {
    await fetch(`${EMP_API}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editRow),
    });
    setEditId(null);
    setEditRow({});
    fetchEmployees();
  };

  const handleIdClick = (emp) => {
    setModalRow(emp);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleModalChange = (e) => {
    const updated = { ...modalRow, [e.target.name]: e.target.value };
    // Auto-populate salary fields on change
    setModalRow(calculateSalaryFields(updated));
  };

  const handleModalSave = async () => {
    if (modalMode === 'add') {
      await fetch(EMP_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(modalRow),
      });
    } else {
      await fetch(`${EMP_API}/${modalRow.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(modalRow),
      });
    }
    setModalOpen(false);
    setModalRow({});
    fetchEmployees();
  };

  const handleAddClick = () => {
    setModalRow(emptyRow);
    setModalMode('add');
    setModalOpen(true);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Employee Management</Typography>
      <Box sx={{ mb: 1 }}>
        <Tooltip title="Add Employee">
          <IconButton color="primary" onClick={handleAddClick}><AddIcon /></IconButton>
        </Tooltip>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>ID</TableCell>
              {columns.map(col => (
                <TableCell key={col.key} sx={{ width: col.width }}>{col.label}</TableCell>
              ))}
              <TableCell sx={{ width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={columns.length + 2}>Loading...</TableCell></TableRow>
            ) : employees.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length + 2}>No employees found.</TableCell></TableRow>
            ) : (
              employees.map(emp => (
                <TableRow key={emp.employee_id}>
                  <TableCell>
                    <Button variant="text" onClick={() => handleIdClick(emp)}>{emp.employee_id}</Button>
                  </TableCell>
                  {columns.map(col => (
                    <TableCell key={col.key} sx={{ width: col.width }}>
                      {editId === emp.id ? (
                        <TextField
                          name={col.key}
                          value={editRow[col.key] || ''}
                          onChange={handleChange}
                          size="small"
                        />
                      ) : (
                        emp[col.key]
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    {editId === emp.id ? (
                      <IconButton color="primary" onClick={() => handleSave(emp.id)}><SaveIcon /></IconButton>
                    ) : (
                      <IconButton color="primary" onClick={() => handleEdit(emp)}><EditIcon /></IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{modalMode === 'add' ? 'Add Employee' : 'Employee Details'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {Object.keys(fieldLabels).map(key => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <TextField
                    label={fieldLabels[key]}
                    name={key}
                    value={modalRow[key] || ''}
                    onChange={handleModalChange}
                    fullWidth
                    InputProps={{ readOnly: ['basic','hra','total_allowances','gross_salary','tds','net_salary'].includes(key) }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleModalSave}>{modalMode === 'add' ? 'Add' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 